
import React, { useState } from 'react';
import { PaymentGateway } from '../PaymentGateway';
import { BookingRecord } from '../../types';
import { Settings, CreditCard, RefreshCw, CheckCircle2, ShieldCheck, Power } from 'lucide-react';
import Swal from 'sweetalert2';

export const PaymentSettings: React.FC = () => {
    const [isSimulating, setIsSimulating] = useState(false);
    const [simAmount, setSimAmount] = useState(35000);

    const mockBooking: BookingRecord = {
        id: `sim_${Date.now()}`,
        code: `TEST-${Math.floor(Math.random() * 1000)}`,
        customerName: 'Admin Tester',
        email: 'admin@test.com',
        phone: '08123456789',
        date: new Date().toISOString().split('T')[0],
        category: 'Personal',
        // Fix: Added missing ticketId property
        ticketId: 'test-ticket-id',
        ticketType: 'Test Transaction',
        pax: 1,
        totalPrice: simAmount,
        status: 'PENDING',
        paymentMethod: 'QRIS',
        bookingDate: new Date().toISOString()
    };

    return (
        <div className="space-y-6 animate-fadeIn">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Payment Gateway</h2>
                    <p className="text-gray-500 text-sm mt-1">Konfigurasi metode pembayaran & simulasi transaksi.</p>
                </div>
                <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1.5 rounded-full border border-green-100 text-sm font-bold">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    Connected: iPaymu
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                {/* Settings Column */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Active Channels */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                        <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <Settings className="w-5 h-5 text-gray-400" /> Saluran Pembayaran Aktif
                        </h3>
                        <div className="space-y-4">
                            {[
                                { name: 'QRIS (Dynamic)', desc: 'GoPay, OVO, Dana, ShopeePay', active: true },
                                { name: 'Virtual Account', desc: 'BCA, Mandiri, BNI, BRI', active: true },
                                { name: 'Retail Outlet', desc: 'Indomaret, Alfamart', active: true },
                                { name: 'Credit Card', desc: 'Visa, Mastercard', active: false },
                            ].map((channel, idx) => (
                                <div key={idx} className="flex items-center justify-between p-4 border border-gray-100 rounded-xl bg-gray-50/50">
                                    <div className="flex items-center gap-3">
                                        <div className={`p-2 rounded-lg ${channel.active ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                                            <CreditCard className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <p className={`font-bold text-sm ${channel.active ? 'text-gray-900' : 'text-gray-400'}`}>{channel.name}</p>
                                            <p className="text-xs text-gray-500">{channel.desc}</p>
                                        </div>
                                    </div>
                                    <div className={`w-10 h-5 rounded-full relative transition-colors cursor-pointer ${channel.active ? 'bg-giz-primary' : 'bg-gray-300'}`}>
                                        <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${channel.active ? 'left-6' : 'left-1'}`}></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Security & Credentials */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                        <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <ShieldCheck className="w-5 h-5 text-gray-400" /> API Configuration
                        </h3>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">API Key</label>
                                <div className="flex gap-2">
                                    <input type="password" value="************************" disabled className="flex-1 bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-500" />
                                    <button className="text-xs font-bold text-giz-primary hover:underline">Regenerate</button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Callback URL</label>
                                <input type="text" value="https://gizpark.com/api/callback/ipaymu" disabled className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-500 font-mono" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Simulator Column */}
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 sticky top-6">
                        <div className="flex items-center gap-2 mb-4">
                            <RefreshCw className="w-5 h-5 text-indigo-600" />
                            <h3 className="font-bold text-gray-900">Payment Simulator</h3>
                        </div>
                        <p className="text-xs text-gray-500 mb-6">
                            Gunakan fitur ini untuk men-simulasikan pengalaman pembayaran pelanggan atau membuat transaksi tes.
                        </p>

                        {!isSimulating ? (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Nominal Transaksi (Rp)</label>
                                    <input
                                        type="number"
                                        value={simAmount}
                                        onChange={(e) => setSimAmount(Number(e.target.value))}
                                        className="w-full border border-gray-300 rounded-lg p-2.5 bg-white text-gray-900 focus:ring-2 focus:ring-giz-primary outline-none font-bold"
                                    />
                                </div>
                                <button
                                    onClick={() => setIsSimulating(true)}
                                    className="w-full py-3 bg-gray-900 text-white rounded-xl font-bold hover:bg-black transition-colors flex items-center justify-center gap-2"
                                >
                                    <Power className="w-4 h-4" /> Mulai Simulasi
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="border border-indigo-100 rounded-xl overflow-hidden shadow-inner">
                                    <PaymentGateway
                                        booking={mockBooking}
                                        onSuccess={() => {
                                            Swal.fire({
                                                icon: 'success',
                                                title: 'Simulasi Berhasil',
                                                text: 'Status webhook: 200 OK',
                                                confirmButtonColor: '#0F172A'
                                            });
                                            setIsSimulating(false);
                                        }}
                                        onBack={() => setIsSimulating(false)}
                                    />
                                </div>
                                <button
                                    onClick={() => setIsSimulating(false)}
                                    className="w-full py-2 text-red-500 font-bold hover:bg-red-50 rounded-lg text-sm"
                                >
                                    Batalkan Simulasi
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
